import { UNSELECTABLE_OPTION_CLASS } from '@disy/ui/select-list';

import { AttributeCategory } from 'cadenza/repository/attribute-category';
import type { HierarchyType } from 'cadenza/repository/model/hierarchy-type.gen';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { AttributeIdOrName } from 'cadenza/workbook/model/attribute-id-or-name';

import './attribute-and-hierarchy-list-renderer.css';

const HIERARCHY_ROOT_CLASS = 'hierarchy-root';
const HIERARCHY_LEVEL_CLASS = 'hierarchy-level';
const HIERARCHY_LAST_LEVEL_CLASS = 'hierarchy-level-last';

export interface RenderableAttributeOrHierarchy {
  id: AttributeIdOrName;
  hierarchyId?: AttributeId;
  category: AttributeCategory;
  hierarchyType?: HierarchyType;
}

/**
 * Renders attributes and hierarchies and additionally adds hierarchy graphical "tree" on the left side.
 * Example:
 * Ort
 * |- Landkreis
 * |_ Gemeinde
 * Caution: element returned by renderAttributeFunction should have "position: relative" to allow to position tree correctly.
 *
 * @param attributes - list of attributes to render
 * @param renderHierarchyFunction - function to render hierarchy element
 * @param renderAttributeFunction - function to render attribute element on the right from tree
 * @param getAttributeByIdFunction
 * @return List of rendered elements. Hierarchy name is a separate element, but all hierarchy levels
 * are also separate. If height of your elements rendered by renderAttributeFunction and renderHierarchyFunction
 * is different than default, you should override styles for .hierarchy-level and .hierarchy-level-last
 */
export function renderAttributesAndHierarchies<T extends RenderableAttributeOrHierarchy>(
  attributes: T[],
  renderHierarchyFunction: (attribute: T) => HTMLElement,
  renderAttributeFunction: (attribute: T) => HTMLElement,
  getAttributeByIdFunction: (attributeId: AttributeId) => T,
) {
  const alreadyRenderedHierarchies = new Set();

  return attributes.flatMap((attribute) => {
    if (attribute.category === AttributeCategory.MEASURE) {
      return [renderAttributeFunction(attribute)];
    }
    if (attribute.category === AttributeCategory.DIMENSION) {
      // this will be only the case if hierarchy attribute is not part of attribute list
      if (attribute.hierarchyId && !alreadyRenderedHierarchies.has(attribute.hierarchyId)) {
        alreadyRenderedHierarchies.add(attribute.hierarchyId);
        const hierarchyAttribute = getAttributeByIdFunction(attribute.hierarchyId); // by definition
        return hierarchyAttribute ?
            renderHierarchyWithAttributes(
              hierarchyAttribute,
              attributes,
              renderHierarchyFunction,
              renderAttributeFunction,
              { hierarchyRootClass: UNSELECTABLE_OPTION_CLASS },
            )
          : [];
      } else if (attribute.hierarchyId) {
        // hierarchy was already processed, no need to do it again
        return [];
      }
      // plain attribute
      return [renderAttributeFunction(attribute)];
    }
    // For (some)? hierarchies there's no category but the there's the HierarchType set
    if (attribute.hierarchyType && !alreadyRenderedHierarchies.has(attribute.id)) {
      alreadyRenderedHierarchies.add(attribute.id);
      return renderHierarchyWithAttributes(attribute, attributes, renderHierarchyFunction, renderAttributeFunction);
    }
    return [];
  });
}

interface RenderHierarchyWithAttributesOptions {
  hierarchyRootClass?: string;
}

/**
 * Renders hierarchies and additionally adds hierarchy graphical "tree" on the left side.
 * Example:
 * Ort
 * |- Landkreis
 * |_ Gemeinde
 * Caution: element returned by renderAttributeFunction should have "position: relative" to allow to position tree correctly.
 *
 * @param hierarchy - hierarchy from the list of attributes
 * @param attributes - list of attributes that contain hierarchy and all its attributes
 * @param renderHierarchyFunction - function to render hierarchy element
 * @param renderAttributeFunction - function to render attribute element on the right from tree
 * @param options - additional parameters for hierarchy rendering
 * @param options.hierarchyRootClass - additional class that will be added to hierarchy root
 * @return List of rendered elements. Hierarchy name is a separate element, but all hierarchy levels
 * are also separate. If height of your elements rendered by renderAttributeFunction and renderHierarchyFunction
 * is different than default, you should override styles for .hierarchy-level and .hierarchy-level-last
 */
function renderHierarchyWithAttributes<T extends RenderableAttributeOrHierarchy>(
  hierarchy: T,
  attributes: T[],
  renderHierarchyFunction: (attribute: T) => HTMLElement,
  renderAttributeFunction: (attribute: T) => HTMLElement,
  { hierarchyRootClass }: RenderHierarchyWithAttributesOptions = {},
) {
  const hierarchyElement = renderHierarchyFunction(hierarchy);
  hierarchyElement.classList.add(HIERARCHY_ROOT_CLASS);
  if (hierarchyRootClass) {
    hierarchyElement.classList.add(hierarchyRootClass);
  }
  const hierarchyAttributeElements = renderHierarchyAttributes(hierarchy, attributes, renderAttributeFunction);
  return [hierarchyElement, ...hierarchyAttributeElements];
}

function renderHierarchyAttributes<T extends RenderableAttributeOrHierarchy>(
  hierarchy: RenderableAttributeOrHierarchy,
  attributes: T[],
  renderAttributeFunction: (attribute: T) => HTMLElement,
) {
  const hierarchyAttributes = attributes.filter((attr) => attr.hierarchyId === hierarchy.id);
  return hierarchyAttributes.map((hierarchyAttribute, index) => {
    const element = renderAttributeFunction(hierarchyAttribute);
    element.classList.add(HIERARCHY_LEVEL_CLASS);
    if (index === hierarchyAttributes.length - 1) {
      element.classList.add(HIERARCHY_LAST_LEVEL_CLASS);
    }
    return element;
  });
}
